import React, { useState, useEffect } from 'react';
import { Printer, Search, FileText, Plus, Calendar, X } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { formatDate } from '../../utils/dateTimeUtils';

const ExperienceCertificate = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    staff_id: '',
    relieving_date: '',
    responsibilities: '',
    conduct: 'Excellent'
  });
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchStaffList();
    fetchCertificates();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = staffList.filter(staff =>
        staff.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        staff.employee_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStaff(filtered);
    } else {
      setFilteredStaff(staffList);
    }
  }, [searchTerm, staffList]);

  const fetchStaffList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaffList(response.data);
      setFilteredStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchCertificates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/staff/experience-certificate`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificates(response.data);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    }
  };

  const handleStaffSelect = (staff) => {
    setSelectedStaff(staff);
    setFormData({
      ...formData,
      staff_id: staff.id
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/staff/experience-certificate`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Experience certificate created successfully!');
      setShowCreateModal(false);
      setSelectedStaff(null);
      setFormData({
        staff_id: '',
        relieving_date: '',
        responsibilities: '',
        conduct: 'Excellent'
      });
      fetchCertificates();
    } catch (error) {
      console.error('Error creating certificate:', error);
      alert(error.response?.data?.detail || 'Failed to create certificate');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (certificate) => {
    // Note: This certificate is designed for letterhead paper
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;

    // Note at top (for letterhead alignment)
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('(Print on Official Letterhead)', pageWidth / 2, 15, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    // Header - Starting lower to leave space for letterhead
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('EXPERIENCE CERTIFICATE', pageWidth / 2, 45, { align: 'center' });

    // Certificate Number
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Certificate No: ${certificate.id.substring(0, 8).toUpperCase()}`, margin, 60);
    doc.text(`Date: ${formatDate(certificate.issue_date)}`, pageWidth - margin, 60, { align: 'right' });

    // Content
    doc.setFontSize(12);
    let yPos = 80;

    const content = `This is to certify that ${certificate.staff_name} (Employee ID: ${certificate.employee_id}) was employed with our organization as ${certificate.designation} in the ${certificate.department} department from ${formatDate(certificate.joining_date)} to ${formatDate(certificate.relieving_date)}.`;

    const lines = doc.splitTextToSize(content, pageWidth - 2 * margin);
    doc.text(lines, margin, yPos);
    yPos += lines.length * 7 + 10;

    if (certificate.responsibilities) {
      doc.text('During the tenure, the employee was responsible for:', margin, yPos);
      yPos += 10;
      const respLines = doc.splitTextToSize(certificate.responsibilities, pageWidth - 2 * margin - 10);
      doc.text(respLines, margin + 5, yPos);
      yPos += respLines.length * 7 + 10;
    }

    const conductText = `The employee's conduct and performance was ${certificate.conduct.toLowerCase()}. We wish ${certificate.staff_name.split(' ')[0]} all the best for future endeavors.`;
    const conductLines = doc.splitTextToSize(conductText, pageWidth - 2 * margin);
    doc.text(conductLines, margin, yPos);
    yPos += conductLines.length * 7 + 20;

    // Signature
    doc.setFont('helvetica', 'bold');
    doc.text('Authorized Signatory', margin, pageHeight - 30);
    doc.setFont('helvetica', 'normal');
    doc.text(certificate.issued_by_name, margin, pageHeight - 20);

    // Save
    doc.save(`Experience_Certificate_${certificate.employee_id}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Experience Certificates</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage experience certificates for staff</p>
        </div>
      </div>

      {/* Search Staff */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search staff by name or employee ID..."
            className="pl-10 pr-4 py-2 w-full border rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Staff List */}
        {searchTerm && filteredStaff.length > 0 && (
          <div className="mt-4 max-h-60 overflow-y-auto border rounded-lg">
            {filteredStaff.slice(0, 5).map((staff) => (
              <div
                key={staff.id}
                onClick={() => handleStaffSelect(staff)}
                className="p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
              >
                <div>
                  <p className="font-medium text-gray-900">{staff.full_name}</p>
                  <p className="text-sm text-gray-500">{staff.employee_id} | {staff.designation} - {staff.department}</p>
                </div>
                <Plus className="h-5 w-5 text-blue-600" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Certificates List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Generated Certificates</h2>
        </div>
        <div className="p-4">
          {certificates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No certificates generated yet</p>
              <p className="text-sm text-gray-400 mt-1">Search for a staff member above to create a certificate</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Staff Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Designation</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Relieving Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {certificates.map((cert) => (
                    <tr key={cert.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cert.staff_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cert.employee_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cert.designation}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(cert.relieving_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(cert.issue_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handlePrint(cert)}
                          className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
                        >
                          <Printer className="h-4 w-4" />
                          Print
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && selectedStaff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create Experience Certificate</h2>
              <button onClick={() => { setShowCreateModal(false); setSelectedStaff(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Staff Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Staff Details</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Name</p>
                    <p className="font-medium">{selectedStaff.full_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Employee ID</p>
                    <p className="font-medium">{selectedStaff.employee_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Designation</p>
                    <p className="font-medium">{selectedStaff.designation}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Department</p>
                    <p className="font-medium">{selectedStaff.department}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Joining Date</p>
                    <p className="font-medium">{formatDate(selectedStaff.joining_date)}</p>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relieving Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                  value={formData.relieving_date}
                  onChange={(e) => setFormData({ ...formData, relieving_date: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsibilities & Achievements
                </label>
                <textarea
                  rows="4"
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Describe key responsibilities and achievements during tenure..."
                  value={formData.responsibilities}
                  onChange={(e) => setFormData({ ...formData, responsibilities: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conduct & Performance
                </label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={formData.conduct}
                  onChange={(e) => setFormData({ ...formData, conduct: e.target.value })}
                >
                  <option value="Excellent">Excellent</option>
                  <option value="Very Good">Very Good</option>
                  <option value="Good">Good</option>
                  <option value="Satisfactory">Satisfactory</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setSelectedStaff(null); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {loading ? 'Creating...' : 'Create Certificate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperienceCertificate;
